import { useCallback, useEffect, useMemo, useState } from "react";
import { Trans } from "react-i18next";
import { t } from "i18next";
import { Image as AntImage, message, Popover, Tooltip } from "antd";
import { isNil, useSerializeCallback } from "@kalodata/tools";
import { IconStepReload, IconVideoFull } from "@icons/index";
import { getPreviewVideoCredit } from "@apis/http/api";
import libraryService from "@apis/libraryService";
import type { JobDetailData } from "@apis/videoCreatingService";
import { CommonButton } from "@components/CommonButton";
import CostPopover from "@components/CostPopover";
import MemberPopoverContent from "@components/MemberPopoverContent";
import Section from "@components/Section";
import { useMembershipLevel } from "@hooks/membership";
import { useAppDispatch, useAppSelector } from "@hooks/redux";
import { fetchVideoGenerateList } from "@pages/library/librarySlice";
import type { UserAssetRecord } from "@apis/http/api";
import { useVideoGenerateCard } from "@pages/library/UICardWrap/hooks";
import { fetchAiCredits } from "@store/globalSlice";
import { GeneratedDetailExportMenu } from "./GeneratedDetailExportMenu";
import { ASPECT_RATIO_MAP, formatDate, getVideoUrl } from "./helpers";

export interface VideoInfoPanelProps {
  record: UserAssetRecord;
  jobDetail: JobDetailData | null;
  detailLoading: boolean;
  onDeleted?: () => void;
  onClose: () => void;
  onPostModalOpenChange?: (open: boolean) => void;
}

export const VideoInfoPanel = ({
  record,
  jobDetail,
  detailLoading,
  onDeleted,
  onClose,
  onPostModalOpenChange,
}: VideoInfoPanelProps) => {
  const input = jobDetail?.input;
  const dispatch = useAppDispatch();
  const { totalRemain } = useAppSelector((state) => state.globalReducer.creditInfo);
  const { isBasicUp } = useMembershipLevel();
  const [retryCost, setRetryCost] = useState<number | null>(null);
  const [retryLoading, setRetryLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const { handleVideoRecreation } = useVideoGenerateCard(record);
  const hasInternalModelId = !!input?.internalModelId;
  const jobId = record?.jobItem?.jobId;
  const status = record?.jobItem?.status;

  const creditParams = useMemo(
    () => ({
      aspectRatio: input?.aspectRatio ?? "",
      duration: input?.duration ?? 0,
      quantity: input?.quantity ?? 1,
      resolution: input?.resolution ?? "",
      internalModelId: input?.internalModelId ?? "",
    }),
    [input?.aspectRatio, input?.duration, input?.internalModelId, input?.quantity, input?.resolution],
  );

  const handleRetryCredit = useCallback(async () => {
    try {
      if (isNil(creditParams.internalModelId) || !creditParams.internalModelId) {
        setRetryCost(0);
        return;
      }
      const res = await getPreviewVideoCredit(creditParams as unknown as any);
      setRetryCost(res || 0);
    } catch {
      setRetryCost(0);
    }
  }, [creditParams]);

  const handleRetry = async () => {
    if (!jobId || retryLoading) return message.error(t("Failed"));
    if (!input?.internalModelId) {
      message.error(t("This model is unavailable. Unable to regenerate."));
      return;
    }
    setRetryLoading(true);
    try {
      await libraryService.resubmitAiJob({
        id: String(jobId),
        internalModelId: input.internalModelId,
      });
      message.success(t("Submitted successfully"));
      dispatch(fetchVideoGenerateList({}));
      dispatch(fetchAiCredits());
    } catch {
      message.error(t("Failed"));
    } finally {
      setRetryLoading(false);
    }
  };

  const handleDelete = async () => {
    if (deleteLoading) return;
    setDeleteLoading(true);
    try {
      if (record.id === undefined) return;
      await libraryService.fetchTrash(String(record.id));
      message.success(t("Moved to trash."));
      await dispatch(fetchVideoGenerateList({}));
      onDeleted?.();
    } catch {
    } finally {
      setDeleteLoading(false);
    }
  };

  const btnDisabled = !isBasicUp || (retryCost !== null && totalRemain < retryCost);
  const retryBtnDisabled = btnDisabled || !hasInternalModelId;
  const anotherBtnDisabled = btnDisabled || !hasInternalModelId;

  const getPopoverContent = () => {
    if (!isBasicUp) return <MemberPopoverContent desc="Upgrade to use the feature" action="Upgrade" />;
    if (retryCost !== null && totalRemain < retryCost)
      return <MemberPopoverContent remainCredit={totalRemain} action="Credits" />;
    return null;
  };

  const renderRetryBtn = () => {
    const btn = (
      <CommonButton
        loading={retryLoading}
        className="w-full"
        disabled={retryBtnDisabled}
        type="primary"
        onClick={handleRetry}
      >
        <IconStepReload
          invert
          className="w-[16px] h-[16px] cursor-pointer text-kds-fg-subtle hover:text-kds-fg transition-colors"
        />
        <Trans>Retry</Trans>
      </CommonButton>
    );

    const popoverContent = getPopoverContent();
    if (popoverContent) {
      return (
        <Popover content={popoverContent} trigger="hover">
          {btn}
        </Popover>
      );
    }

    if (!hasInternalModelId) {
      return (
        <Tooltip title={<Trans>This model is unavailable. Unable to regenerate.</Trans>} trigger="hover">
          {btn}
        </Tooltip>
      );
    }

    return (
      <CostPopover text={t("Retry")} cost={retryCost || "-"}>
        {btn}
      </CostPopover>
    );
  };

  const reGenerateVideo = useSerializeCallback(async () => {
    await handleVideoRecreation();
    onClose();
  });

  const renderAnotherBtn = () => {
    const btn = (
      <CommonButton loading={retryLoading} className="w-full" disabled={anotherBtnDisabled} onClick={reGenerateVideo}>
        <Trans>Generate Another</Trans>
      </CommonButton>
    );

    const popoverContent = getPopoverContent();
    if (popoverContent) {
      return (
        <Popover content={popoverContent} trigger="hover">
          {btn}
        </Popover>
      );
    }

    if (!hasInternalModelId) {
      return (
        <Tooltip title={<Trans>This model is unavailable. Unable to regenerate.</Trans>} trigger="hover">
          {btn}
        </Tooltip>
      );
    }

    return (
      <CostPopover text={t("Generate Another")} cost={retryCost || "-"} open={!anotherBtnDisabled}>
        {btn}
      </CostPopover>
    );
  };

  useEffect(() => {
    if (input) handleRetryCredit();
  }, [handleRetryCredit, input]);

  const videoUrl = getVideoUrl(jobDetail, record);
  const assetId = record?.id ? Number(record.id) : undefined;

  return (
    <Section
      status={detailLoading ? "loading" : "succeeded"}
      className="flex flex-col h-full overflow-y-auto bg-kds-bg"
    >
      <div className="px-[24px] pt-[16px]">
        {/* Badge */}
        <div className="flex items-center gap-[4px] py-[4px] leading-[22px] pb-[12px]">
          <IconVideoFull className="shrink-0 w-[20px] h-[20px]" />
          <span className="font-bold text-[16px] text-kds-fg">
            <Trans>Video Generation</Trans>
          </span>
        </div>
        <div className="flex border-b border-kds-border"></div>
      </div>
      <div className="flex flex-col gap-[12px] p-[24px] py-[12px] flex-1 overflow-auto">
        {/* Description */}
        <div className="flex flex-col gap-[8px]">
          <div className="text-[16px] font-bold text-kds-fg leading-[22px] py-[4px]">
            <Trans>Video Script</Trans>
          </div>
          <div className="text-[14px] text-kds-fg wrap-break-word whitespace-pre-wrap max-h-[30vh] overflow-y-auto">
            {input?.userModifiedScript || input?.originScript || "-"}
          </div>
        </div>

        <div className="flex border-b border-kds-border"></div>

        {/* Basic Information */}
        <div className="flex flex-col gap-[4px]">
          <div className="text-[16px] font-bold text-kds-fg leading-[22px] py-[4px]">
            <Trans>Basic Info</Trans>
          </div>
          <div className="flex items-center gap-[12px] flex-wrap">
            <Tooltip title={<Trans>The AI model used for this video.</Trans>}>
              <span className="text-[14px] text-kds-fg">{t(input?.modelCode || "-")}</span>
            </Tooltip>
            <div className="flex border-l border-kds-border h-[16px]"></div>
            <span className="text-[14px] text-kds-fg">{ASPECT_RATIO_MAP[input?.aspectRatio || "RATIO_9_16"]}</span>
            <div className="flex border-l border-kds-border h-[16px]"></div>
            <span className="text-[14px] text-kds-fg">{`${input?.duration || "-"}s`}</span>
            <div className="flex border-l border-kds-border h-[16px]"></div>
            <span className="text-[14px] text-kds-fg">{`${input?.resolution || "-"}`}</span>
            <div className="flex border-l border-kds-border h-[16px]"></div>
            <span className="text-[14px] text-kds-fg">{formatDate(jobDetail?.gmtCreated)}</span>
          </div>
        </div>

        <div className="flex border-b border-kds-border"></div>

        {/* Product Information */}
        <div className="flex flex-col gap-[4px]">
          <div className="text-[16px] font-bold text-kds-fg leading-[22px] py-[4px]">
            <Trans>Product Information</Trans>
          </div>
          <div className="flex gap-[8px] flex-wrap">
            {input?.imageInfos.map((img: any, index) => (
              <div
                key={img.id || index}
                className="bg-kds-bg-element border border-kds-border w-[54px] h-[54px] rounded-[4px] overflow-hidden"
              >
                <AntImage alt="basic" src={img.imageUrl} className="object-contain w-full h-full" />
              </div>
            ))}
          </div>
          <div className="text-[14px] text-kds-fg-subtle wrap-break-word line-clamp-3 mt-[4px]">
            {input?.productTitle}
          </div>
        </div>
      </div>

      {/* Actions */}
      {status === "completed" && (
        <div className="mt-auto px-[24px] py-[16px] grid grid-cols-2 gap-[8px] border-t border-kds-border">
          {renderAnotherBtn()}
          <CommonButton
            onClick={() => jobId && window.open(`/video-creating?jobId=${jobId}`, "_blank")}
            disabled={!jobId}
          >
            <Trans>Edit Settings</Trans>
          </CommonButton>
          <CommonButton onClick={handleDelete} loading={deleteLoading}>
            <Trans>Delete</Trans>
          </CommonButton>
          {assetId && (
            <GeneratedDetailExportMenu
              assetId={assetId}
              disabled={!videoUrl}
              download={{
                videoURL: videoUrl,
                videoId: String(record.id),
                kalo_type: "generated_detail",
              }}
              onPostModalOpenChange={onPostModalOpenChange}
            />
          )}
        </div>
      )}
      {status === "failed" && (
        <div className="mt-auto px-[24px] py-[16px] grid grid-cols-2 gap-[8px] border-t border-kds-border">
          <CommonButton onClick={handleDelete} loading={deleteLoading}>
            <Trans>Delete</Trans>
          </CommonButton>
          <CommonButton
            onClick={() => jobId && window.open(`/video-creating?jobId=${jobId}`, "_blank")}
            disabled={!jobId}
          >
            <Trans>Edit Settings</Trans>
          </CommonButton>
          <div className="col-span-2">{renderRetryBtn()}</div>
        </div>
      )}
    </Section>
  );
};
